import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BarChart3, Bell, BookOpen, CalendarCheck, Check, ChevronRight, Clock3, CreditCard, DoorOpen, MessageCircle, ShieldCheck, Users } from "lucide-react";
import { Logo } from "../components/Primitives";
import { inr } from "../lib/api";

function Feature({ icon, title, text }) {
  return (
    <article className="feature-card" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="feature-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{text}</p>
      <ArrowRight className="feature-arrow" size={18} />
    </article>
  );
}

function durationLabel(days) {
  if (!days || days <= 0) return "custom";
  if (days === 365) return "year";
  if (days === 90) return "90 days";
  if (days === 30) return "month";
  if (days === 14) return "14 days";
  if (days % 365 === 0) return `${days / 365} years`;
  if (days % 30 === 0) return `${days / 30} months`;
  return `${days} days`;
}

function PlanCard({ plan, highlight }) {
  const items = [
    `Up to ${plan.max_students || "unlimited"} students`,
    `Up to ${plan.max_seats || "unlimited"} seats`,
    `Up to ${plan.max_books || "unlimited"} books`,
    `Up to ${plan.max_staff || "unlimited"} staff`,
    ...(plan.features || []),
  ];
  return (
    <article className={`pricing-card ${highlight ? "pricing-highlight" : ""}`} data-testid={`pricing-${plan.code?.toLowerCase() || plan.name.toLowerCase()}`}>
      <header>
        <p className="hero-label">{plan.name.toUpperCase()}</p>
        <h3>{inr(plan.price)}</h3>
        <small>per {durationLabel(plan.duration_days)}</small>
      </header>
      <ul>
        {items.map((item, idx) => (
          <li key={`${item}-${idx}`}>
            <Check size={15} /> {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/plans/public`)
      .then((response) => setPlans(response.data.data || []))
      .catch(() => setPlans([]));
  }, []);
  return (
    <div className="landing" data-testid="landing-page">
      <nav className="public-nav">
        <Logo />
        <div className="public-links">
          <a href="#features" data-testid="features-nav-link">Capabilities</a>
          <a href="#workflow" data-testid="workflow-nav-link">How it works</a>
          <a href="#pricing" data-testid="pricing-nav-link">Plans</a>
          <a href="#faq" data-testid="faq-nav-link">FAQ</a>
        </div>
        <button className="button button-outline" onClick={() => navigate("/login")} data-testid="landing-login-button">
          Sign in <ArrowRight size={16} />
        </button>
      </nav>

      <main>
        <section className="hero-section">
          <div className="hero-copy">
            <p className="hero-label">MULTI-TENANT LIBRARY OPERATIONS</p>
            <h1>
              Every library.<br />
              <span>One clear system.</span>
            </h1>
            <p className="hero-description">
              LibraryOS brings students, seats, shifts, payments, books, and daily attendance into one focused workspace built for independent libraries.
            </p>
            <div className="hero-actions">
              <button className="button button-primary" onClick={() => navigate("/login")} data-testid="hero-get-started-button">
                Open your workspace <ArrowRight size={17} />
              </button>
              <a
                className="button button-whatsapp"
                href="https://wa.me/918228025545?text=Hi%20LibraryOS%2C%20I%20need%20help%20with..."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
              >
                <MessageCircle size={17} /> WhatsApp support
              </a>
              <a className="text-link" href="#features" data-testid="hero-explore-link">
                Explore capabilities <ChevronRight size={16} />
              </a>
            </div>
            <div className="hero-proof">
              <ShieldCheck size={18} />
              <span>Tenant-isolated data and role-aware access from day one</span>
            </div>
          </div>
          <div className="hero-visual">
            <img
              src="https://images.unsplash.com/photo-1760166699654-5d0e10f51994?auto=format&fit=crop&w=1200&q=85"
              alt="Quiet modern library study room"
              data-testid="hero-library-image"
            />
            <div className="visual-caption">
              <span>Built for calm, precise operations</span>
              <strong>Students · Seats · Shifts</strong>
            </div>
          </div>
        </section>

        <section className="metrics-strip" data-testid="platform-metrics">
          <div><ShieldCheck size={22} /><span>Tenant-isolated by design</span></div>
          <div><Users size={22} /><span>Student, seat & shift control</span></div>
          <div><CreditCard size={22} /><span>Billing with auto-generated receipts</span></div>
        </section>

        <section className="feature-section" id="features">
          <div className="section-heading">
            <p className="hero-label">THE OPERATING LAYER</p>
            <h2>Less admin noise.<br />More room to run the library.</h2>
            <p>Each module is designed around the moments library teams repeat every single day.</p>
          </div>
          <div className="feature-grid">
            <Feature icon={<Users />} title="Student command" text="Admissions, renewals, profiles, fees and seat assignments in one dependable record." />
            <Feature icon={<DoorOpen />} title="Seat clarity" text="Live floor plan with available, occupied, reserved and maintenance seats." />
            <Feature icon={<Clock3 />} title="Shift planner" text="Create unlimited shifts and watch capacity fill up in real time." />
            <Feature icon={<CreditCard />} title="Auto billing" text="Monthly bills auto-generate with overdue tracking and PDF-ready receipts." />
            <Feature icon={<BookOpen />} title="Book issue" text="Issue, return and track every book copy against the student record." />
            <Feature icon={<CalendarCheck />} title="Attendance" text="Daily check-ins per shift feeding directly into reports." />
          </div>
        </section>

        <section className="workflow-section" id="workflow">
          <div className="workflow-visual">
            <img
              src="https://images.unsplash.com/photo-1566268485756-cdd81c47f65c?auto=format&fit=crop&w=1000&q=85"
              alt="Books and library desk"
              data-testid="workflow-library-image"
            />
          </div>
          <div className="workflow-copy">
            <p className="hero-label">READY FOR THE NEXT SHIFT</p>
            <h2>Start with a sharper view of today.</h2>
            <p>Log in as Main Admin or library staff. Your workspace opens with the metrics and actions that match your role.</p>
            <div className="check-list">
              <div><Check size={17} /> Multi-library management with isolation</div>
              <div><Check size={17} /> Automated monthly billing + receipts</div>
              <div><Check size={17} /> Role & permission aware staff access</div>
              <div><Check size={17} /> Daily cron for reminders and overdue flags</div>
            </div>
            <button className="button button-dark" onClick={() => navigate("/login")} data-testid="workflow-login-button">
              Enter LibraryOS <ArrowRight size={17} />
            </button>
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <div className="section-heading" style={{ textAlign: "left", maxWidth: 1280, margin: "0 auto", padding: "0 32px 24px" }}>
            <p className="hero-label">PLANS</p>
            <h2>Simple, honest pricing.</h2>
            <p>Each tier is a real plan available to Main Admins when provisioning a library tenant.</p>
          </div>
          <div className="pricing-grid">
            {plans.length === 0 ? (
              <small style={{ color: "#64748b", padding: "0 32px" }}>Plan details load ho rahe hain…</small>
            ) : (
              plans.map((plan, idx) => (
                <PlanCard key={plan.id || plan.code} plan={plan} highlight={plans.length > 1 && idx === 1} />
              ))
            )}
          </div>
        </section>

        <section className="faq-section" id="faq">
          <div className="section-heading" style={{ maxWidth: 760, margin: "0 auto" }}>
            <p className="hero-label">FAQ</p>
            <h2>Everything a library owner usually asks.</h2>
          </div>
          <div className="faq-grid">
            <article><h4>Can my staff see other libraries?</h4><p>No. Each library tenant is strictly isolated at the database layer.</p></article>
            <article><h4>How does monthly billing work?</h4><p>Bills generate automatically from every student's joining date and renew every month.</p></article>
            <article><h4>Do you support offline/manual payments?</h4><p>Yes. Record cash, UPI, bank transfer or card payments with auto-generated receipts.</p></article>
            <article><h4>Who runs the scheduled jobs?</h4><p>Our managed daily cron handles overdue flags, expiry reminders and renewal nudges.</p></article>
          </div>
        </section>
      </main>

      <footer className="public-footer">
        <Logo />
        <span>© {new Date().getFullYear()} LibraryOS · Powered by MKS Digital Technology</span>
        <span>Built for independent libraries</span>
      </footer>
    </div>
  );
}
